import { useState } from 'react';
import Card from '../../../../shared/Card';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import { List } from './types';
import { getItems, getRelationships } from './api/items-and-relationships-api';
import { useContext } from 'react';
import AppContext from '../../../../AppContext';
import Spinner from 'react-bootstrap/Spinner';

type SortColumn = 'name' | 'items' | 'relationships' | 'lastOpenedAt';
type SortDirection = 'asc' | 'desc';

function ListsList({
  currentListId,
  lists,
  onClick,
}: {
  currentListId: string;
  lists: { [id: string]: List };
  onClick: (listId: string) => void;
}) {
  const { user } = useContext(AppContext);
  const [listStats, setListStats] = useState<
    Record<string, { items: number; relationships: number }>
  >({});
  const [loadingStats, setLoadingStats] = useState<Record<string, boolean>>({});
  const [sortColumn, setSortColumn] = useState<SortColumn>('lastOpenedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Load stats for each list
  const loadStats = async (listId: string) => {
    if (!user?.uid || loadingStats[listId]) return;

    setLoadingStats((prev) => ({ ...prev, [listId]: true }));
    try {
      const [items, relationships] = await Promise.all([
        getItems(user.uid, listId),
        getRelationships(user.uid, listId),
      ]);

      setListStats((prev) => ({
        ...prev,
        [listId]: {
          items: Object.keys(items).length,
          relationships: Object.keys(relationships).length,
        },
      }));
    } finally {
      setLoadingStats((prev) => ({ ...prev, [listId]: false }));
    }
  };

  if (!lists) {
    return null;
  }

  const formatDate = (timestamp: number | undefined) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(
        column === 'items' || column === 'relationships' ? 'desc' : 'asc'
      );
    }
  };

  const getSortedLists = () => {
    return Object.values(lists).sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'items':
          comparison =
            (listStats[a.id]?.items || 0) - (listStats[b.id]?.items || 0);
          break;
        case 'relationships':
          comparison =
            (listStats[a.id]?.relationships || 0) -
            (listStats[b.id]?.relationships || 0);
          break;
        case 'lastOpenedAt':
          // Handle missing or invalid dates by putting them at the end
          if (!a.lastOpenedAt || isNaN(new Date(a.lastOpenedAt).getTime()))
            return 1;
          if (!b.lastOpenedAt || isNaN(new Date(b.lastOpenedAt).getTime()))
            return -1;
          comparison = a.lastOpenedAt - b.lastOpenedAt;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const SortableHeader = ({
    column,
    label,
  }: {
    column: SortColumn;
    label: string;
  }) => (
    <th onClick={() => handleSort(column)} style={{ cursor: 'pointer' }}>
      {label}
      {sortColumn === column && (
        <span className="ms-1">{sortDirection === 'desc' ? '↓' : '↑'}</span>
      )}
    </th>
  );

  const LoadingSpinner = () => (
    <Spinner
      animation="border"
      size="sm"
      role="status"
      style={{ width: '1rem', height: '1rem' }}
    >
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  );

  return (
    <Table hover>
      <thead>
        <tr>
          <SortableHeader column="name" label="Name" />
          <SortableHeader column="items" label="Items" />
          <SortableHeader column="relationships" label="Relationships" />
          <SortableHeader column="lastOpenedAt" label="Last Opened" />
        </tr>
      </thead>
      <tbody>
        {getSortedLists().map((list) => {
          const active = currentListId === list.id;
          const isLoading = loadingStats[list.id];

          // Load stats if not already loaded and not currently loading
          if (!listStats[list.id] && !isLoading) {
            loadStats(list.id);
          }

          return (
            <tr
              key={list.id}
              onClick={() => onClick(list.id)}
              style={{
                cursor: 'pointer',
                backgroundColor: active ? '#0d6efd' : undefined,
                color: active ? 'white' : undefined,
              }}
            >
              <td>{list.name}</td>
              <td>
                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  listStats[list.id]?.items || 0
                )}
              </td>
              <td>
                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  listStats[list.id]?.relationships || 0
                )}
              </td>
              <td>{formatDate(list.lastOpenedAt)}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

const AddListForm = ({
  onSubmit,
}: {
  onSubmit: ({
    listName,
    onSuccess,
  }: {
    listName: string;
    onSuccess: () => void;
  }) => void;
}) => {
  const [newListName, setNewListName] = useState<string>('');
  const [error, setError] = useState<string>('');

  const clearNewList = () => {
    setNewListName('');
    setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newListName.trim()) {
      setError('List name cannot be empty');
      return;
    }

    onSubmit({ listName: newListName.trim(), onSuccess: clearNewList });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <ButtonGroup style={{ width: '100%' }}>
        <Form.Control
          type="text"
          placeholder="list name"
          value={newListName}
          onChange={(e) => {
            setNewListName(e.target.value);
            setError('');
          }}
          autoFocus={true}
          isInvalid={!!error}
        />
        <Button
          style={{ whiteSpace: 'nowrap' }}
          type="submit"
          disabled={!newListName.trim()}
        >
          Create List
        </Button>
      </ButtonGroup>
      {error && <Form.Text className="text-danger">{error}</Form.Text>}
    </Form>
  );
};

function Lists({
  createList,
  currentListId,
  lists,
  selectListId,
}: {
  createList: (name: string, onSuccess: () => void) => void;
  currentListId: string;
  lists: { [id: string]: List };
  selectListId: (listId: string) => void;
}) {
  return (
    <Card>
      <div className="mb-3">
        <ListsList
          currentListId={currentListId}
          lists={lists}
          onClick={selectListId}
        />
      </div>
      <AddListForm
        onSubmit={({ listName, onSuccess = () => null }) => {
          createList(listName, onSuccess);
        }}
      />
    </Card>
  );
}

export default Lists;
